import Input from 'lib/Input/Input';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { Store } from 'components/common/types/Store.types';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { changeActiveObjectiveName } from 'store/objective/reducer';
import { getIsProgrammaticEndedAddNewLineEnabled } from 'store/dealManagement/selectors/getIsProgrammaticEndedAddNewLineEnabled';
import { ObjectiveNameProps } from './ObjectiveName.types';

const ObjectiveName: React.FC<ObjectiveNameProps> = ({ error, onError }) => {
  const dispatch = useDispatch();
  const isProgrammaticEndedAddNewLineEnabled = useSelector(getIsProgrammaticEndedAddNewLineEnabled);
  const { isLineReadOnly } = useIsReadOnly({ isEditingAllowed: isProgrammaticEndedAddNewLineEnabled });

  const objectives = useSelector((state: Store) => state.dealManagement.directSales.objectives);
  const activeObjectiveGroupId = useSelector((state: Store) => state.objective.data.objectiveGroupId);
  const activeObjectiveName = useSelector((state: Store) => state.objective.data.name);

  const existingObjectivesNames = useMemo(() => {
    return objectives.map(({ name }) => name.toLowerCase());
  }, [objectives]);

  const initialObjectiveName = useMemo(() => {
    return objectives.find((objective) => objective.objectiveGroupId === activeObjectiveGroupId)?.name;
  }, [objectives, activeObjectiveGroupId]);

  useEffect(() => {
    const isInitialName = activeObjectiveName === initialObjectiveName;

    if (!isInitialName && existingObjectivesNames.includes(activeObjectiveName.toLowerCase())) {
      onError('This objective name already exists');
    }
  }, [existingObjectivesNames, activeObjectiveName, initialObjectiveName]);

  const onObjectiveNameChange = useCallback(
    (newObjectiveName: string): void => {
      onError('');
      dispatch(changeActiveObjectiveName(newObjectiveName));

      if (!newObjectiveName.length) {
        onError('Objective name cannot be empty');
        return;
      }

      const isInitialName = newObjectiveName === initialObjectiveName;

      if (!isInitialName && existingObjectivesNames.includes(newObjectiveName.toLowerCase())) {
        onError('This objective name already exists');
      }
    },
    [initialObjectiveName, existingObjectivesNames],
  );

  return (
    <Input
      value={activeObjectiveName}
      onChange={onObjectiveNameChange}
      isDisabled={isLineReadOnly}
      errorMessage={error}
      placeholder="Type here..."
      dataTestId="objective-name-input"
      id="objectiveName"
      label="Name"
      maxLength={60}
    />
  );
};

export default ObjectiveName;
